import { useState, useEffect, useCallback } from 'react';
import { Match } from '@/lib/database';

interface HomepageBundle {
  liveMatches: Match[];
  upcomingMatches: Match[];
  featuredMatch?: Match;
  matchCounts: {
    total: number;
    live: number;
    today: number;
    tomorrow: number;
  };
}

interface UseHomepageResult {
  // Main data
  bundle: HomepageBundle | null;
  
  // Individual components for easy access
  liveMatches: Match[];
  upcomingMatches: Match[];
  featuredMatch?: Match;
  matchCounts: {
    total: number;
    live: number;
    today: number;
    tomorrow: number;
  };
  
  // Loading states
  loading: boolean;
  error: string | null;
  fromCache: boolean;
  
  // Performance info
  performance?: {
    total_time: number;
    live_count: number;
    upcoming_count: number;
    has_featured: boolean;
  };
  
  // Actions
  refetch: () => void;
}

// Singleton cache and request tracking to prevent duplicate calls
class HomepageDataManager {
  private cache = new Map<string, { data: HomepageBundle; timestamp: number }>();
  private pendingRequests = new Map<string, Promise<any>>();
  private readonly TTL = 5 * 60 * 1000; // 5 minutes

  async getData(): Promise<any> {
    const cacheKey = 'homepage-data';
    
    // Check if there's already a pending request
    const pendingRequest = this.pendingRequests.get(cacheKey);
    if (pendingRequest) {
      return pendingRequest;
    }

    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < this.TTL) {
      // Return full result object for consistency
      return {
        data: cached.data,
        _fromCache: true,
        performance: { from_client_cache: true }
      };
    }

    // Create new request
    const requestPromise = this.fetchData();
    this.pendingRequests.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      
      // Cache the result data
      this.cache.set(cacheKey, {
        data: result.data,
        timestamp: Date.now()
      });

      return result;
    } finally {
      // Remove pending request
      this.pendingRequests.delete(cacheKey);
    }
  }

  private async fetchData(): Promise<any> {
    const response = await fetch('/api/homepage-data', {
      signal: AbortSignal.timeout(30000),
    });
    
    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch homepage data');
    }
    
    return result;
  }

  clearCache(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }
}

const homepageManager = new HomepageDataManager();

export function useHomepage(): UseHomepageResult {
  const [bundle, setBundle] = useState<HomepageBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [performance, setPerformance] = useState<any>(undefined);

  const fetchHomepageData = useCallback(async (forceRefresh = false) => {
    try {
      setError(null);
      const startTime = Date.now();

      if (forceRefresh) {
        homepageManager.clearCache();
      }

      // Use singleton manager to prevent duplicate requests
      const result = await homepageManager.getData();
      
      // result is the full API response: { data: HomepageBundle, _fromCache, performance }
      const bundleData = result.data as HomepageBundle;
      setBundle(bundleData);
      setFromCache(result._fromCache || false);
      setPerformance(result.performance);

    } catch (err) {
      console.error('Homepage fetch error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  const refetch = useCallback(() => {
    // Force refresh without clearing cache (for comparison)
    fetchHomepageData(true);
  }, [fetchHomepageData]);

  useEffect(() => {
    fetchHomepageData();
  }, [fetchHomepageData]);

  // Individual components for easy access
  const individualComponents = {
    liveMatches: bundle?.liveMatches || [],
    upcomingMatches: bundle?.upcomingMatches || [],
    featuredMatch: bundle?.featuredMatch,
    matchCounts: bundle?.matchCounts || {
      total: 0,
      live: 0,
      today: 0,
      tomorrow: 0
    }
  };

  return {
    bundle,
    ...individualComponents,
    loading,
    error,
    fromCache,
    performance,
    refetch
  };
}

// Prefetch homepage data for faster loading
export function prefetchHomepageData() {
  // Prefetch using the singleton manager to avoid duplicate requests
  homepageManager.getData().catch(error => {
    console.warn('Homepage prefetch failed:', error);
  });
}
